import React, { useState } from "react";
import {
    FormLayout,
    HeaderActions,
    Heading,
    LoadingProgress,
    Panel,
    Text,
    Textarea,
} from "@/ui";
import { Icon } from "@/components";
import {
    useForm,
    useFormActions,
    useMeta,
    useNotification,
    useValidator,
} from "@/hooks";
import {
    MUTATION_UPDATE_METAFIELD_APP,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import _ from "lodash";
import isJSON from "validator/lib/isJSON";
import { JsonTreeEditor, PencilIcon } from "./shared";
import styles from "./GeneralsForm.module.scss";
import redirectStyles from "./RedirectForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;
const DiagramIcon = () => <i aria-hidden className="fas fa-project-diagram" />;

const MetafieldForm = ({ app }) => {
    const { user } = useMeta();
    const { inputs, setInputs, genericChange } = useForm();
    const [free, setFree] = useState(false);
    const forceUpdate = useState(0)[1];
    const notify = useNotification();

    const validate = () => {
        const json = document.getElementsByName("metafield")[0].value;

        if (isJSON(json)) return true;

        notify("Invalid Metafield structure.", "error");

        return false;
    };

    const switchMode = () => {
        const valid = free ? validate() : true;

        if (!valid) return;

        if (!free)
            document.getElementsByName("metafield")[0].value = JSON.stringify(
                inputs.metafield,
                null,
                4,
            );

        setFree(current => !current);
    };

    const changeOnFree = e => {
        const value = e.target.value;

        if (isJSON(value)) {
            genericChange(JSON.parse(value), e.target.name);
        } else {
            forceUpdate(update => update + 1);
        }
    };

    if (!_.get(inputs, "id", null)) {
        setInputs({
            ...app,
            metafield: JSON.parse(app.metafield || "{}"),
        });
    }

    const options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                metafield: JSON.stringify(inputs.metafield),
                type: "Metafield",
            },
        },
    };

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "metafield",
                method: "isJSON",
                validWhen: true,
                args: [],
                message: "Invalid Metafield structure.",
            },
        ],
        {
            ...inputs,
            metafield: free
                ? document.getElementsByName("metafield")[0]?.value
                : JSON.stringify(inputs.metafield),
        },
    );

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Metafield",
        "",
        MUTATION_UPDATE_METAFIELD_APP,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "primary",
    );

    const metafieldError = validator.metafield?.isInvalid
        ? validator.metafield.message
        : undefined;

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Metafield</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Metafield Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "mode",
                                label: !free ? "Free mode" : "Visual mode",
                                icon: !free ? <PencilIcon /> : <DiagramIcon />,
                                variant: "secondary",
                                onClick: switchMode,
                            },
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: () => createOrUpdate(),
                            },
                        ]}
                        aria-label="Metafield actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            {user.loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    {!free ? (
                        <FormLayout.Section title="Metadata Settings">
                            <FormLayout.Grid columns={1}>
                                <JsonTreeEditor
                                    onChange={json => {
                                        genericChange(json, "metafield");
                                    }}
                                    rootName="metafield"
                                    value={
                                        inputs.metafield ? inputs.metafield : {}
                                    }
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>
                    ) : null}
                    <div
                        className={
                            !free ? redirectStyles.hiddenField : undefined
                        }
                    >
                        <Textarea
                            error={metafieldError}
                            fullWidth
                            label="Metadata Settings"
                            name="metafield"
                            onChange={changeOnFree}
                            rows={40}
                            textareaClassName={redirectStyles.freeTextarea}
                        />
                    </div>
                </FormLayout>
            )}
        </Panel>
    );
};

export default MetafieldForm;
